import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Menu } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { useSidebarCollapsed } from '@/lib/useSidebarCollapsed';
import { LiveIndicator, RealtimeProvider } from '@/lib/realtime/RealtimeStatus';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { useUnreadCards } from '@/features/board/useUnreadCards';

/**
 * Sidebar beside the content on desktop, behind a drawer on phones.
 *
 * One <Sidebar />, rendered twice. Two implementations of a nav is how a link ends up
 * on one and not the other.
 */
export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { collapsed, toggle } = useSidebarCollapsed();
  const unread = useUnreadCards();
  const location = useLocation();

  // A drawer that survives navigation covers the page you just asked for.
  useEffect(() => setDrawerOpen(false), [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  return (
    <RealtimeProvider>
    <div className="flex h-full" style={{ background: 'var(--canvas)' }}>
      <aside className="hidden shrink-0 lg:block">
        <Sidebar collapsed={collapsed} onToggleCollapse={toggle} />
      </aside>

      {/*
        Always mounted, never conditionally rendered. Unmounting is instantaneous by
        definition — there is no closed state left on screen to animate out of — so the
        drawer lives here permanently and only its transform changes.

        `inert` while closed is what makes that safe: without it the links stay in the
        tab order and a screen reader reads a menu nobody can see.
      */}
      <div
        className={`fixed inset-0 z-50 lg:hidden ${drawerOpen ? 'drawer-open' : 'pointer-events-none'}`}
        {...(drawerOpen ? {} : { inert: true })}
      >
        <button
          type="button"
          aria-label="Close menu"
          tabIndex={drawerOpen ? 0 : -1}
          onClick={() => setDrawerOpen(false)}
          className="drawer-scrim absolute inset-0 bg-black/40"
        />
        {/* Never collapsed: an icon rail you must open a drawer to see is pointless. */}
        <div className="drawer-panel relative h-full w-max shadow-xl">
          <Sidebar onNavigate={() => setDrawerOpen(false)} />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* The bar exists on mobile only — on desktop the sidebar is the chrome. */}
        <header
          className="flex shrink-0 items-center gap-2 border-b px-3 py-2.5 lg:hidden"
          style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
        >
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="-ml-1 flex size-10 items-center justify-center rounded-lg"
            style={{ color: 'var(--text-muted)' }}
          >
            <Menu size={20} />
          </button>
          <img src="/logo.svg" alt="" width={22} height={22} className="dark-invert"
               style={{ width: 22, height: 22 }} />
          <span className="text-sm font-semibold tracking-tight">Tap Academy</span>

          {/*
            Connection state and notifications both belong to the app rather than to any
            one screen, so they live with the app's chrome.

            The bell is HERE on mobile and in the sidebar on desktop. Inside the drawer
            it was effectively invisible: a badge you have to open a menu to notice is a
            badge that tells you nothing.
          */}
          <span className="ml-auto flex items-center gap-1">
            <LiveIndicator />

            {/*
              The board's own header is desktop-only now, so this is the only place the
              count appears on a phone. Next to the bell rather than in the strip below,
              because the two say related things and reading them together is the point.
            */}
            {unread !== null && unread > 0 && (
              <span
                className="rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums"
                style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                title="Tasks assigned to you with something you have not read"
              >
                {unread} unread
              </span>
            )}

            <NotificationBell variant="header" />
          </span>
        </header>

        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
    </RealtimeProvider>
  );
}
